

import React, {
    useEffect,
    useRef,
    useState
} from "react";

import DailyIframe from "@daily-co/daily-js";

import {
    JWT
} from "../../../../constants/local-storage";

import EvaLiveConversationDrawer from "./EvaLiveConversationDrawer";


//========================================================
// Eva Live Expert - Tavus
//
// Tavus provides:
// - Fiona's video avatar
// - Microphone / listening
// - Speech generation
// - Live conversation
//
// OmniScan provides:
// - AI intelligence through Tavus backend tool
// - Live conversation transcript
//
// Layout:
// - Existing Simli expert layout
// - Fiona video on left
// - Conversation drawer on right
//========================================================

const API_BASE =
    "/resolutionsoftwareplatform/eva/tavus";

const POLL_INTERVAL = 1000;


//========================================================
// Component
//========================================================

export default function EvaLiveExpertTavus()
{

    //----------------------------------------------------
    // References
    //----------------------------------------------------

    const videoContainerRef = useRef(null);

    const dailyRef = useRef(null);

    const conversationIdRef = useRef(null);

    const sessionRef = useRef(0);

    const startingRef = useRef(false);

    const mountedRef = useRef(false);

    const pollingRef = useRef(null);

    const pollingBusyRef = useRef(false);


    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [started, setStarted] = useState(false);

    const [connecting, setConnecting] = useState(false);

    const [connected, setConnected] = useState(false);

    const [error, setError] = useState(null);

    const [messages, setMessages] = useState([]);


    //====================================================
    // Authentication Headers
    //====================================================

    const getHeaders = () =>
    {

        return {
            "Authorization": localStorage.getItem(JWT) || "",
            "Accept": "application/json",
            "Content-Type": "application/json"
        };

    };


    //====================================================
    // Stop Transcript Polling
    //====================================================

    const stopTranscriptPolling = () =>
    {

        if (pollingRef.current !== null)
        {

            clearInterval(
                pollingRef.current
            );

            pollingRef.current = null;

        }

    };


    //====================================================
    // Fetch Conversation Messages
    //====================================================

    const fetchConversationMessages = async (
        conversationId,
        session
    ) =>
    {

        if (
            !conversationId ||
            pollingBusyRef.current
        )
        {
            return;
        }

        pollingBusyRef.current = true;

        try
        {

            //------------------------------------------------
            // Retrieve Transcript From OmniScan
            //------------------------------------------------

            const response = await fetch(
                API_BASE +
                    "/conversation/" +
                    encodeURIComponent(conversationId) +
                    "/messages",
                {
                    method: "GET",
                    headers: getHeaders(),
                    cache: "no-store"
                }
            );


            if (!response.ok)
            {

                throw new Error(
                    "Transcript request failed. HTTP " +
                    response.status
                );

            }


            const transcript =
                await response.json();


            //------------------------------------------------
            // Ignore Results From Old Conversations
            //------------------------------------------------

            if (
                !mountedRef.current ||
                session !== sessionRef.current ||
                conversationIdRef.current !== conversationId
            )
            {
                return;
            }


            //------------------------------------------------
            // Update Conversation Drawer
            //------------------------------------------------

            if (Array.isArray(transcript))
            {

                setMessages(previous =>
                {

                    //------------------------------------------------
                    // Avoid Unnecessary React Updates
                    //------------------------------------------------

                    if (
                        previous.length === transcript.length &&
                        previous.every(
                            (message, index) =>
                                message.id === transcript[index].id &&
                                message.role === transcript[index].role &&
                                message.text === transcript[index].text
                        )
                    )
                    {
                        return previous;
                    }

                    //------------------------------------------------
                    // Convert Assistant Role To Fiona
                    //
                    // Existing drawer uses "fiona" for
                    // assistant message styling.
                    //------------------------------------------------

                    return transcript.map(
                        message => ({
                            ...message,
                            role:
                                message.role === "assistant"
                                    ? "fiona"
                                    : message.role
                        })
                    );

                });

            }

        }
        catch (exception)
        {

            if (
                mountedRef.current &&
                session === sessionRef.current
            )
            {

                console.warn(
                    "Unable to retrieve Fiona transcript:",
                    exception
                );

            }

        }
        finally
        {

            pollingBusyRef.current = false;

        }

    };


    //====================================================
    // Start Transcript Polling
    //====================================================

    const startTranscriptPolling = (
        conversationId,
        session
    ) =>
    {

        //------------------------------------------------
        // Stop Any Previous Polling
        //------------------------------------------------

        stopTranscriptPolling();


        //------------------------------------------------
        // Fetch Immediately
        //------------------------------------------------

        fetchConversationMessages(
            conversationId,
            session
        );


        //------------------------------------------------
        // Poll Once Per Second
        //------------------------------------------------

        pollingRef.current = setInterval(
            () =>
            {

                if (
                    !mountedRef.current ||
                    session !== sessionRef.current ||
                    conversationIdRef.current !== conversationId
                )
                {

                    stopTranscriptPolling();

                    return;

                }

                fetchConversationMessages(
                    conversationId,
                    session
                );

            },
            POLL_INTERVAL
        );

    };


    //====================================================
    // End Backend Conversation
    //====================================================

    const endBackendConversation = async conversationId =>
    {

        if (!conversationId)
        {
            return;
        }

        try
        {

            const response = await fetch(
                API_BASE +
                    "/conversation/" +
                    encodeURIComponent(conversationId) +
                    "/end",
                {
                    method: "POST",
                    headers: getHeaders()
                }
            );


            if (!response.ok)
            {

                console.warn(
                    "Unable to end Tavus conversation:",
                    response.status
                );

            }

        }
        catch (exception)
        {

            console.warn(
                "Tavus conversation cleanup failed:",
                exception
            );

        }

    };


    //====================================================
    // Destroy Daily Connection
    //====================================================

    const destroyDaily = async () =>
    {

        const daily = dailyRef.current;

        dailyRef.current = null;

        if (!daily)
        {
            return;
        }

        try
        {

            await daily.destroy();

        }
        catch (exception)
        {

            console.warn(
                "Unable to destroy Daily connection:",
                exception
            );

        }

    };


    //====================================================
    // Stop Live Expert
    //====================================================

    const stopLiveExpert = async () =>
    {

        //------------------------------------------------
        // Invalidate Current Session
        //------------------------------------------------

        ++sessionRef.current;

        startingRef.current = false;


        //------------------------------------------------
        // Stop Transcript Polling
        //------------------------------------------------

        stopTranscriptPolling();


        //------------------------------------------------
        // Capture Conversation ID
        //------------------------------------------------

        const conversationId =
            conversationIdRef.current;

        conversationIdRef.current = null;


        //------------------------------------------------
        // Reset UI State
        //------------------------------------------------

        setStarted(false);

        setConnecting(false);

        setConnected(false);

        setError(null);


        //------------------------------------------------
        // Destroy Tavus Video Connection
        //------------------------------------------------

        await destroyDaily();


        //------------------------------------------------
        // End Conversation On Backend
        //------------------------------------------------

        await endBackendConversation(
            conversationId
        );

    };


    //====================================================
    // Start Live Expert
    //====================================================

    const startLiveExpert = async () =>
    {

        if (
            startingRef.current ||
            dailyRef.current
        )
        {
            return;
        }


        //------------------------------------------------
        // Create New Session
        //------------------------------------------------

        const session =
            ++sessionRef.current;

        startingRef.current = true;


        //------------------------------------------------
        // Reset Conversation
        //------------------------------------------------

        stopTranscriptPolling();

        setMessages([]);

        setStarted(true);

        setConnecting(true);

        setConnected(false);

        setError(null);


        let createdConversationId = null;

        let daily = null;


        try
        {

            //------------------------------------------------
            // Create Tavus Conversation Through OmniScan
            //------------------------------------------------

            const response = await fetch(
                API_BASE + "/conversation",
                {
                    method: "POST",
                    headers: getHeaders()
                }
            );


            if (!response.ok)
            {

                throw new Error(
                    "Unable to create Tavus conversation. HTTP " +
                    response.status
                );

            }


            const conversation =
                await response.json();


            const conversationId =
                conversation.conversation_id;


            const conversationUrl =
                conversation.conversation_url;


            if (
                !conversationId ||
                !conversationUrl
            )
            {

                throw new Error(
                    "Tavus did not return a conversation ID and URL."
                );

            }


            createdConversationId =
                conversationId;


            //------------------------------------------------
            // Validate Tavus Conversation URL
            //------------------------------------------------

            const parsedUrl =
                new URL(conversationUrl);


            if (
                parsedUrl.protocol !== "https:" ||
                parsedUrl.hostname !== "tavus.daily.co"
            )
            {

                throw new Error(
                    "Unexpected Tavus conversation URL."
                );

            }


            //------------------------------------------------
            // Handle User Ending During Startup
            //------------------------------------------------

            if (
                !mountedRef.current ||
                session !== sessionRef.current
            )
            {

                await endBackendConversation(
                    createdConversationId
                );

                return;

            }


            //------------------------------------------------
            // Register Active Conversation In React
            //------------------------------------------------

            conversationIdRef.current =
                createdConversationId;


            //------------------------------------------------
            // Start Transcript Polling
            //------------------------------------------------

            startTranscriptPolling(
                createdConversationId,
                session
            );


            //------------------------------------------------
            // Validate Video Container
            //------------------------------------------------

            if (!videoContainerRef.current)
            {

                throw new Error(
                    "Fiona's video container is unavailable."
                );

            }


            //------------------------------------------------
            // Create Daily Video Frame
            //------------------------------------------------

            daily = DailyIframe.createFrame(
                videoContainerRef.current,
                {

                    iframeStyle: {
                        width: "100%",
                        height: "100%",
                        border: "0",
                        backgroundColor: "#080b15"
                    },

                    showLeaveButton: false,

                    showLocalVideo: false,

                    showParticipantsBar: false

                }
            );


            dailyRef.current =
                daily;


            //------------------------------------------------
            // Daily Events
            //------------------------------------------------

            daily.on(
                "joined-meeting",
                () =>
                {

                    if (
                        !mountedRef.current ||
                        session !== sessionRef.current
                    )
                    {
                        return;
                    }

                    setConnecting(false);

                    setConnected(true);

                }
            );


            daily.on(
                "left-meeting",
                () =>
                {

                    if (
                        session !== sessionRef.current
                    )
                    {
                        return;
                    }

                    stopLiveExpert();

                }
            );


            daily.on(
                "error",
                event =>
                {

                    if (
                        session !== sessionRef.current
                    )
                    {
                        return;
                    }

                    console.error(
                        "Tavus Daily error:",
                        event
                    );

                    setError(
                        "The Fiona video connection encountered an error."
                    );

                    setConnecting(false);

                }
            );


            //------------------------------------------------
            // Join Tavus Conversation
            //------------------------------------------------

            await daily.join({
                url: conversationUrl,
                audioSource: true,
                videoSource: false
            });


            //------------------------------------------------
            // Check Session Still Active
            //------------------------------------------------

            if (
                !mountedRef.current ||
                session !== sessionRef.current
            )
            {
                return;
            }


            //------------------------------------------------
            // Connected
            //------------------------------------------------

            setConnecting(false);

            setConnected(true);

        }
        catch (exception)
        {

            console.error(
                "Unable to start Tavus Fiona:",
                exception
            );


            //------------------------------------------------
            // Update UI If This Session Is Still Current
            //------------------------------------------------

            if (
                mountedRef.current &&
                session === sessionRef.current
            )
            {

                ++sessionRef.current;

                stopTranscriptPolling();

                setStarted(false);

                setConnecting(false);

                setConnected(false);

                setError(
                    exception.message ||
                    "Unable to start Fiona."
                );

            }


            //------------------------------------------------
            // Cleanup Daily Connection
            //------------------------------------------------

            if (daily)
            {

                if (dailyRef.current === daily)
                {
                    dailyRef.current = null;
                }

                try
                {

                    await daily.destroy();

                }
                catch (cleanupError)
                {

                    console.warn(
                        "Daily cleanup failed:",
                        cleanupError
                    );

                }

            }


            //------------------------------------------------
            // Cleanup Backend Conversation
            //------------------------------------------------

            if (createdConversationId)
            {

                if (
                    conversationIdRef.current ===
                    createdConversationId
                )
                {
                    conversationIdRef.current = null;
                }

                await endBackendConversation(
                    createdConversationId
                );

            }

        }
        finally
        {

            if (session === sessionRef.current)
            {
                startingRef.current = false;
            }

        }

    };


    //====================================================
    // Component Cleanup
    //====================================================

    useEffect(
        () =>
        {

            mountedRef.current = true;

            return () =>
            {

                mountedRef.current = false;

                ++sessionRef.current;

                startingRef.current = false;

                stopTranscriptPolling();


                //------------------------------------------------
                // Capture Conversation
                //------------------------------------------------

                const conversationId =
                    conversationIdRef.current;

                conversationIdRef.current = null;


                //------------------------------------------------
                // Capture Daily Instance
                //------------------------------------------------

                const daily =
                    dailyRef.current;

                dailyRef.current = null;


                //------------------------------------------------
                // Destroy Daily
                //------------------------------------------------

                if (daily)
                {

                    daily.destroy().catch(
                        exception =>
                        {

                            console.warn(
                                "Daily cleanup:",
                                exception
                            );

                        }
                    );

                }


                //------------------------------------------------
                // End Backend Conversation
                //------------------------------------------------

                if (conversationId)
                {

                    endBackendConversation(
                        conversationId
                    );

                }

            };

        },
        []
    );


    //====================================================
    // Typed Messages
    //
    // Disabled until Tavus text interaction is wired.
    //====================================================

    const sendTypedMessage = async text =>
    {

        console.warn(
            "Typed Tavus messages are not enabled yet:",
            text
        );

        throw new Error(
            "Typed messages are not enabled yet."
        );

    };


    //====================================================
    // Render
    //
    // IMPORTANT:
    // Same layout hierarchy as working Simli version.
    // No nested expert/layout wrappers.
    //====================================================

    return (

        <div className="eva-live-expert">

            {/*========================================
              Fiona Video Area
            ========================================*/}

            <div className="eva-live-expert-video">

                {/*====================================
                  Tavus Video Container

                  Keep mounted while the call starts
                  and throughout the conversation.
                ====================================*/}

                <div
                    ref={videoContainerRef}
                    style={{
                        display: started
                            ? "block"
                            : "none",
                        width: "100%",
                        height: "100%",
                        minHeight: "440px"
                    }}
                />


                {/*====================================
                  Start Screen
                ====================================*/}

                {!started && (

                    <div className="eva-live-expert-start">

                        <div className="eva-demo-handover-eyebrow">
                            EVA LIVE
                        </div>

                        <div className="eva-demo-handover-title">
                            Talk to an OmniScanPro Expert
                        </div>

                        <div className="eva-demo-handover-text">
                            Have a live conversation with
                            Fiona, your OmniScanPro
                            virtual expert.
                        </div>

                        <button
                            type="button"
                            className="eva-demo-primary-button"
                            onClick={startLiveExpert}
                        >
                            Start Conversation
                        </button>

                    </div>

                )}


                {/*====================================
                  Connection Status
                ====================================*/}

                {started && (

                    <div className="eva-live-expert-connected">

                        <span
                            className="eva-demo-status-dot"
                        />

                        {connecting
                            ? "Connecting"
                            : connected
                                ? "Live"
                                : "Waiting for Fiona"}

                    </div>

                )}


                {/*====================================
                  End Conversation
                ====================================*/}

                {started && (

                    <button
                        type="button"
                        className="eva-live-expert-end-button"
                        onClick={stopLiveExpert}
                    >
                        End Conversation
                    </button>

                )}


                {/*====================================
                  Error
                ====================================*/}

                {error && (

                    <div className="eva-live-expert-error">
                        {error}
                    </div>

                )}

            </div>


            {/*========================================
              Existing Fiona Conversation Drawer
            ========================================*/}

            <EvaLiveConversationDrawer
                open={started}
                messages={messages}
                onSend={sendTypedMessage}
                connected={false}
            />

        </div>

    );

}
